import { isJsonObject, type JsonObject } from "./jsonDecoding";

/**
 * RFC 9457 problem details as the DMW server sends them (`server/src/dmw/kernel/errors.py`,
 * `ProblemDetails`): the standard members plus DMW's `code`, `request_id` and `details`.
 */
export interface ProblemDetails {
  /** `urn:dmw:error:<code>`. */
  readonly type: string;
  readonly title: string;
  readonly status: number;
  /** Explanation of this occurrence, in English. Shown only as technical detail. */
  readonly detail: string;
  readonly instance: string;
  /** Stable machine-readable error code. The UI keys its messages on this, never on text. */
  readonly code: string;
  /** Empty if the response carried none. */
  readonly requestId: string;
  readonly details: JsonObject;
}

export const PROBLEM_JSON_MEDIA_TYPE = "application/problem+json";

/**
 * Read a parsed JSON body as problem details. Returns `null` when the body is not DMW
 * problem details, so the caller reports an unexpected response instead of guessing.
 *
 * `code` and an integer `status` are required: without them the UI cannot say what
 * happened. The other members are required by the server contract too, but are read
 * leniently, so that a proxy or an older server still yields a usable error.
 */
export function parseProblemDetails(body: unknown): ProblemDetails | null {
  if (!isJsonObject(body)) {
    return null;
  }
  const { status, code, details } = body;
  if (typeof status !== "number" || !Number.isInteger(status)) {
    return null;
  }
  if (typeof code !== "string" || code.length === 0) {
    return null;
  }
  return {
    type: textMember(body, "type") ?? "about:blank",
    title: textMember(body, "title") ?? "",
    status,
    detail: textMember(body, "detail") ?? "",
    instance: textMember(body, "instance") ?? "",
    code,
    requestId: textMember(body, "request_id") ?? "",
    details: isJsonObject(details) ? details : {},
  };
}

/** A member that should be text; anything else counts as missing. */
function textMember(object: JsonObject, member: string): string | undefined {
  const value = object[member];
  return typeof value === "string" ? value : undefined;
}
